import moment from 'moment'

// ─────────────────────────────────────────────────────────────────────────────
// Mapeo del layout de empleados del software de nómina externo (82 columnas).
//
// Este módulo es la ÚNICA fuente de verdad del layout: lo consumen tanto
// "Importar Desde Software Externo" (solo altas) como "Edición Masiva Desde
// Software Externo" (altas + ediciones). Arreglar un mapeo aquí arregla los dos.
//
// No importa React ni nada del store: son funciones puras y se pueden probar
// con un script de Node contra un archivo real.
//
// EL CAMINO DE VUELTA vive en otros dos archivos y hay que moverlos junto con
// éste cuando cambie una codificación:
//   · el orden y los encabezados de las 74 columnas:
//     Pages/Panel/ReportesNomina/layoutCatalogoExterno.js
//   · las codificaciones inversas (Sexo M/F, Base de cotización F/V/M, Estatus
//     A/B/R, Estado Civil, clave RENAPO): erp_backend/Controllers/
//     reportesNomina.controllers.js, que es quien tiene los catálogos a la mano.
// ─────────────────────────────────────────────────────────────────────────────

export const NOMBRE_HOJA = 'Empleados'

// Columnas del layout externo que no tienen ningún campo equivalente en el
// modelo Empleado. Se listan tal cual para informar al usuario qué no se importa.
export const COLUMNAS_NO_MAPEABLES = [
  'Zona de salario', 'Población', 'Campo extra 2', 'Campo extra 3',
  'Campo extra numérico 2', 'Campo extra numérico 3', 'Campo extra numérico 4',
  'Campo extra numérico 5', 'Fecha salario mixto', 'Salario mixto',
  'Tipo de semana reducida', 'Dias vacaciones tomadas antes de alta',
  'Dias prima vacacional tomadas antes de alta', 'Teletrabajador', 'Equipo',
  'Insumo', 'DireccionTeletrabajo', 'Empleado en Colabora',
]

// ── Normalización ────────────────────────────────────────────────────────────

// Quita acentos, colapsa espacios, cambia guiones bajos por espacios y pasa a
// minúsculas. Se usa tanto para los encabezados del Excel como para comparar
// contra los nombres de los catálogos, para que "De_Ley" empate con "De Ley" y
// "SEMANAL " con "Semanal".
const DIACRITICOS = new RegExp('[\u0300-\u036f]', 'g')

export function normalizarTexto(valor) {
  return String(valor ?? '')
    .normalize('NFD')
    .replace(DIACRITICOS, '')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase()
}

const soloDigitos = (s) => /^\d+$/.test(s)

// Compara claves de catálogo tolerando ceros a la izquierda: el Excel externo
// trae "01" y algunos catálogos SAT guardan el número 1.
const mismaClave = (a, b) => {
  if (!a || !b) return false
  if (a === b) return true
  return soloDigitos(a) && soloDigitos(b) && Number(a) === Number(b)
}

export const normalizarRegistroPatronal = (valor) =>
  String(valor ?? '').toUpperCase().replace(/[\s-]/g, '')

// El archivo real trae encabezados con espacios al final ("Aviso pendiente baja
// IMSS ") que hacían que esas columnas nunca se leyeran. El índice mapea el
// encabezado normalizado a la llave cruda que usa el objeto de la fila.
export function construirIndiceEncabezados(filas) {
  const indice = new Map()
  for (const fila of filas) {
    for (const clave of Object.keys(fila)) {
      if (clave === '__rowNum__') continue
      const norm = normalizarTexto(clave)
      if (!indice.has(norm)) indice.set(norm, clave)
    }
  }
  return indice
}

// Devuelve un lector de columnas por nombre, insensible a acentos, caso y
// espacios sobrantes. Una celda vacía devuelve undefined (no cadena vacía) para
// que un campo en blanco no sobreescriba el valor existente.
export function crearLector(fila, indice) {
  return (nombreColumna) => {
    const clave = indice.get(normalizarTexto(nombreColumna))
    if (clave === undefined) return undefined
    const valor = fila[clave]
    if (valor === undefined || valor === null) return undefined
    const texto = String(valor).trim()
    return texto === '' ? undefined : texto
  }
}

// ── Parseo de valores ────────────────────────────────────────────────────────

// El sistema externo escribe la fecha cero de Excel cuando el campo está vacío.
const FECHAS_CENTINELA = new Set([
  '30/12/1899', '30-12-1899', '1899-12-30', '1899/12/30',
  '00/01/1900', '01/01/1900', '1900-01-01',
])

const FORMATOS_FECHA = [
  'YYYY-MM-DD', 'DD/MM/YYYY', 'MM/DD/YYYY', 'DD-MM-YYYY',
  'YYYY/MM/DD', 'D/M/YYYY', 'DD/MM/YY',
]

export function parsearFechaExterna(valor) {
  if (!valor) return undefined
  const str = String(valor).trim()
  if (FECHAS_CENTINELA.has(str)) return undefined
  for (const fmt of FORMATOS_FECHA) {
    const m = moment(str, fmt, true)
    if (m.isValid()) {
      // Cualquier fecha anterior a 1900 es la fecha cero de Excel disfrazada.
      if (m.year() < 1900) return undefined
      return m.format('YYYY-MM-DD')
    }
  }
  return undefined
}

export function parsearNumeroExterno(valor) {
  if (valor === undefined || valor === null || valor === '') return undefined
  const n = Number(String(valor).replace(/[$\s,]/g, ''))
  return isNaN(n) ? undefined : n
}

const VALORES_SI = ['SI', 'SÍ', 'S', 'YES', 'Y', '1', 'TRUE', 'X']
export const esSiExterno = (valor) =>
  valor !== undefined && valor !== null &&
  VALORES_SI.includes(String(valor).trim().toUpperCase())

// ── Enumerados ───────────────────────────────────────────────────────────────

// M = Masculino = Hombre. Verificado contra el carácter 11 del CURP en el
// archivo real: 587 de 602 filas con Sexo="M" traen "H" en el CURP.
export function mapearSexoExterno(valor) {
  if (!valor) return undefined
  const v = normalizarTexto(valor)
  if (['h', 'm', 'hombre', 'masculino', 'male'].includes(v)) return 'Hombre'
  if (['f', 'mujer', 'femenino', 'female'].includes(v)) return 'Mujer'
  if (['na', 'n/a', 'no aplica'].includes(v)) return 'No Aplica'
  return undefined
}

export function mapearEstadoCivilExterno(valor) {
  if (!valor) return undefined
  const mapa = {
    's': 1, 'soltero': 1, 'soltera': 1, '1': 1,
    'c': 2, 'casado': 2, 'casada': 2, '2': 2,
    'co': 3, 'concubinato': 3, '3': 3,
    'u': 4, 'union civil': 4, '4': 4,
    'v': 5, 'viudo': 5, 'viuda': 5, '5': 5,
    'd': 6, 'divorciado': 6, 'divorciada': 6, '6': 6,
  }
  return mapa[normalizarTexto(valor)] ?? undefined
}

// Enum compartido por basePago y baseDeCotizacion:
// 1 Sueldo · 2 Comisión · 3 Destajo · 4 Sueldo-Comisión · 5 Sueldo-Destajo
const MAPA_BASE_PAGO = {
  'sueldo': 1, 'salario': 1, 's': 1, '1': 1, '01': 1,
  'comision': 2, 'c': 2, '2': 2, '02': 2,
  'destajo': 3, 'd': 3, '3': 3, '03': 3,
  'sueldo-comision': 4, 'sueldo comision': 4, 'sc': 4, '4': 4, '04': 4,
  'sueldo-destajo': 5, 'sueldo destajo': 5, 'sd': 5, '5': 5, '05': 5,
}

export function mapearBasePagoExterno(valor) {
  if (!valor) return undefined
  return MAPA_BASE_PAGO[normalizarTexto(valor)] ?? undefined
}

// El software externo maneja la base de cotización del IMSS como Fijo/Variable/
// Mixto, pero el modelo la guarda con el mismo enum 1-5 que la base de pago.
// Se traduce al equivalente más cercano y se aceptan además los valores del enum.
export function mapearBaseCotizacionExterno(valor) {
  if (!valor) return undefined
  const v = normalizarTexto(valor)
  if (['f', 'fijo', 'fija'].includes(v)) return 1
  if (['v', 'variable'].includes(v)) return 2
  if (['m', 'mixto', 'mixta'].includes(v)) return 4
  return mapearBasePagoExterno(valor)
}

export const ETIQUETAS_BASE = {
  1: 'Sueldo', 2: 'Comisión', 3: 'Destajo',
  4: 'Sueldo-Comisión', 5: 'Sueldo-Destajo',
}

export const ETIQUETAS_ESTADO_CIVIL = {
  1: 'Soltero', 2: 'Casado', 3: 'Concubinato',
  4: 'Unión Civil', 5: 'Viudo', 6: 'Divorciado',
}

export const ETIQUETAS_AVISOS = {
  alta: 'Alta', baja: 'Baja', modifSalario: 'Modificación de Salario',
}

// ── Entidad federativa de nacimiento ─────────────────────────────────────────

// El layout externo usa las claves RENAPO de 2 letras (las mismas del CURP),
// que no coinciden con el nombre ni con la abreviatura SAT de la colección Estado.
export const ESTADOS_RENAPO = {
  AS: ['Aguascalientes'],
  BC: ['Baja California'],
  BS: ['Baja California Sur'],
  CC: ['Campeche'],
  CL: ['Coahuila', 'Coahuila de Zaragoza'],
  CM: ['Colima'],
  CS: ['Chiapas'],
  CH: ['Chihuahua'],
  DF: ['Ciudad de México', 'Distrito Federal', 'CDMX'],
  DG: ['Durango'],
  GT: ['Guanajuato'],
  GR: ['Guerrero'],
  HG: ['Hidalgo'],
  JC: ['Jalisco'],
  MC: ['México', 'Estado de México'],
  MN: ['Michoacán', 'Michoacán de Ocampo'],
  MS: ['Morelos'],
  NT: ['Nayarit'],
  NL: ['Nuevo León'],
  OC: ['Oaxaca'],
  PL: ['Puebla'],
  QT: ['Querétaro'],
  QR: ['Quintana Roo'],
  SP: ['San Luis Potosí'],
  SL: ['Sinaloa'],
  SR: ['Sonora'],
  TC: ['Tabasco'],
  TS: ['Tamaulipas'],
  TL: ['Tlaxcala'],
  VZ: ['Veracruz', 'Veracruz de Ignacio de la Llave'],
  YN: ['Yucatán'],
  ZS: ['Zacatecas'],
  NE: [],
}

// Caracteres 12 y 13 del CURP: la entidad de nacimiento.
export function claveEstadoDesdeCurp(curp) {
  if (!curp) return undefined
  const c = String(curp).trim().toUpperCase()
  if (c.length < 13) return undefined
  const clave = c.substring(11, 13)
  return ESTADOS_RENAPO[clave] ? clave : undefined
}

const buscarEstado = (estados, candidatos) => {
  for (const candidato of candidatos) {
    const objetivo = normalizarTexto(candidato)
    const encontrado = estados.find(
      (e) => normalizarTexto(e.nombre) === objetivo ||
             normalizarTexto(e.abreviatura) === objetivo
    )
    if (encontrado) return encontrado
  }
  return undefined
}

// Resuelve la entidad de nacimiento por clave RENAPO, por nombre, o por
// abreviatura; si la columna viene vacía se deduce del propio CURP.
export function resolverEstadoNacimiento(valor, curp, estados = []) {
  let clave = valor ? String(valor).trim().toUpperCase() : undefined
  if (!clave || !ESTADOS_RENAPO[clave]) {
    const desdeCurp = claveEstadoDesdeCurp(curp)
    if (!clave && desdeCurp) clave = desdeCurp
  }
  if (!clave) return undefined
  if (ESTADOS_RENAPO[clave]) {
    const encontrado = buscarEstado(estados, ESTADOS_RENAPO[clave])
    if (encontrado) return encontrado
  }
  return buscarEstado(estados, [clave])
}

// ── Baja y avisos IMSS ───────────────────────────────────────────────────────

const ESTATUS_BAJA = ['B', 'BAJA', 'INACTIVO', 'I']

// Prioriza el estatus explícito; si no viene, usa fecha de baja/reingreso.
export function debeOmitirsePorBaja({ estatus, fechaBaja, fechaReingreso }) {
  const estatusNorm = estatus ? String(estatus).trim().toUpperCase() : ''
  if (estatusNorm) return ESTATUS_BAJA.includes(estatusNorm)
  return Boolean(fechaBaja) && !fechaReingreso
}

// El layout externo trae 4 banderas de aviso ante el IMSS y el sistema tiene un
// solo campo. Se colapsan por prioridad: baja > alta (reingreso) > modifSalario.
export function colapsarAvisosPendientes({ modifSbc, reingreso, baja, cambioBaseCotizacion }) {
  const marcados = []
  if (esSiExterno(baja)) marcados.push('baja')
  if (esSiExterno(reingreso)) marcados.push('alta')
  if (esSiExterno(modifSbc)) marcados.push('modifSalario')
  if (esSiExterno(cambioBaseCotizacion)) marcados.push('modifSalario')

  if (!marcados.length) return { avisosPendientes: undefined, perdidos: 0 }
  const prioridad = ['baja', 'alta', 'modifSalario']
  const elegido = prioridad.find((p) => marcados.includes(p))
  const perdidos = new Set(marcados).size - 1
  return { avisosPendientes: elegido, perdidos: Math.max(perdidos, 0) }
}

// ── Resolución de catálogos ──────────────────────────────────────────────────

const claveCatalogo = (item) =>
  item.c_TipoContrato ?? item.c_FormaPago ?? item.c_TipoJornada ??
  item.c_RegimenFiscal ?? item.c_TipoRegimen ?? item.abreviatura ?? ''

// Busca por nombre/descripción o por clave SAT, indistintamente.
function buscarEnCatalogo(valor, catalogo) {
  const v = normalizarTexto(valor)
  if (!v) return undefined
  return catalogo.find((item) => {
    const nombre = normalizarTexto(item.nombre ?? item.Descripcion ?? item.descripcion ?? '')
    const clave = normalizarTexto(claveCatalogo(item))
    return (nombre && nombre === v) || mismaClave(clave, v)
  })
}

// ── Descripción declarativa de los campos sincronizables ─────────────────────
//
// Cada entrada describe un campo que viaja del Excel al modelo Empleado. La usan
// la vista previa (para mostrar "antes → después"), el cálculo del diff y la
// lista blanca que valida el backend.
//   clave  — llave dentro del payload que arma mapearFilaExterna
//   campo  — campo del modelo Empleado contra el que se compara
//   tipo   — cómo se compara y cómo se muestra
export const CAMPOS_SINCRONIZABLES = [
  { clave: 'numeroClave', campo: 'codigo', label: 'Código', columna: 'Código', tipo: 'numero' },
  { clave: 'nombre', campo: 'nombre', label: 'Nombre', columna: 'Nombre', tipo: 'texto' },
  { clave: 'apellidoPaterno', campo: 'apellidoPaterno', label: 'Apellido Paterno', columna: 'Apellido paterno', tipo: 'texto' },
  { clave: 'apellidoMaterno', campo: 'apellidoMaterno', label: 'Apellido Materno', columna: 'Apellido materno', tipo: 'texto' },
  // Solo cambia cuando el empleado se emparejó por nombre: si se emparejó por
  // RFC, por definición ya es el mismo.
  { clave: 'rfc', campo: 'rfc', label: 'RFC', columna: 'RFC', tipo: 'texto' },
  { clave: 'curp', campo: 'curp', label: 'CURP', columna: 'CURP', tipo: 'texto' },
  { clave: 'numSeguridadSocial', campo: 'numSeguridadSocial', label: 'Núm. Seguridad Social', columna: 'Num seguridad social', tipo: 'texto' },
  { clave: 'sexo', campo: 'sexo', label: 'Sexo', columna: 'Sexo', tipo: 'texto' },
  { clave: 'estadoCivil', campo: 'estadoCivil', label: 'Estado Civil', columna: 'Estado Civil', tipo: 'enumNum', etiquetas: ETIQUETAS_ESTADO_CIVIL },
  { clave: 'fechaNacimiento', campo: 'fechaNacimiento', label: 'Fecha Nacimiento', columna: 'Fecha de nacimiento', tipo: 'fecha' },
  { clave: 'nombrePadre', campo: 'nombrePadre', label: 'Nombre del Padre', columna: 'Nombre del padre', tipo: 'texto' },
  { clave: 'nombreMadre', campo: 'nombreMadre', label: 'Nombre de la Madre', columna: 'Nombre de la madre', tipo: 'texto' },
  { clave: 'ciudadNacimientoId', campo: 'ciudadNacimientoId', label: 'Ciudad de Nacimiento', columna: 'Ciudad de nacimiento', tipo: 'texto' },
  { clave: 'estadoNacimientoId', campo: 'estadoNacimientoId', label: 'Estado de Nacimiento', columna: 'Entidad federativa de nacimiento', tipo: 'catalogo', catalogo: 'estados', muestra: 'nombre' },
  { clave: 'telefono', campo: 'telefonos', label: 'Teléfono', columna: 'Teléfono', tipo: 'telefono' },
  { clave: 'correoElectronico', campo: 'correoElectronico', label: 'Correo Electrónico', columna: 'Correo electrónico', tipo: 'texto' },
  { clave: 'calle', campo: 'calle', label: 'Dirección', columna: 'Dirección', tipo: 'texto' },
  { clave: 'cp', campo: 'cp', label: 'Código Postal', columna: 'CP', tipo: 'texto' },
  { clave: 'fechaAlta', campo: 'fechaAlta', label: 'Fecha de Alta', columna: 'Fecha de alta', tipo: 'fecha' },
  { clave: 'fechaReingreso', campo: 'fechaReingreso', label: 'Fecha de Reingreso', columna: 'Fecha de reingreso', tipo: 'fecha' },
  { clave: 'ultimaBaja', campo: 'ultimaBaja', label: 'Causa Última Baja', columna: 'Causa de la última baja', tipo: 'texto' },
  { clave: 'salarioDiario', campo: 'salarioDiario', label: 'Salario Diario', columna: 'Salario diario', tipo: 'numero' },
  { clave: 'sbcParteFija', campo: 'sbcParteFija', label: 'SBC Parte Fija', columna: 'SBC parte fija', tipo: 'numero' },
  { clave: 'salarioVariable', campo: 'salarioVariable', label: 'Salario Variable', columna: 'Salario variable', tipo: 'numero' },
  { clave: 'salarioPromedio', campo: 'salarioPromedio', label: 'Salario Promedio', columna: 'Salario promedio', tipo: 'numero' },
  { clave: 'salarioBaseLiquidacion', campo: 'salarioBaseLiquidacion', label: 'Salario Base Liquidación', columna: 'Salario base liquidación', tipo: 'numero' },
  { clave: 'saldoAjusteNeto', campo: 'saldoAjusteNeto', label: 'Saldo Ajuste al Neto', columna: 'Saldo del ajuste al neto', tipo: 'numero' },
  { clave: 'fechaSalarioDiario', campo: 'fechaSalarioDiario', label: 'Fecha Salario Diario', columna: 'Fecha del salario diario', tipo: 'fecha' },
  { clave: 'fechaSalarioIntegrado', campo: 'fechaSalarioIntegrado', label: 'Fecha SBC Parte Fija', columna: 'Fecha SBC parte fija', tipo: 'fecha' },
  { clave: 'fechaSalarioVariable', campo: 'fechaSalarioVariable', label: 'Fecha Salario Variable', columna: 'Fecha salario variable', tipo: 'fecha' },
  { clave: 'fechaSalarioPromedio', campo: 'fechaSalarioPromedio', label: 'Fecha Salario Promedio', columna: 'Fecha salario promedio', tipo: 'fecha' },
  { clave: 'basePago', campo: 'basePago', label: 'Base de Pago', columna: 'Base de pago', tipo: 'enumNum', etiquetas: ETIQUETAS_BASE },
  { clave: 'baseDeCotizacion', campo: 'baseDeCotizacion', label: 'Base de Cotización', columna: 'Base de cotización', tipo: 'enumNum', etiquetas: ETIQUETAS_BASE },
  { clave: 'sindicalizado', campo: 'sindicalizado', label: 'Sindicalizado', columna: 'Sindicalizado', tipo: 'bool' },
  { clave: 'calculoPTU', campo: 'calculoPTU', label: 'Cálculo PTU', columna: 'Cálculo PTU', tipo: 'bool' },
  { clave: 'calculoAguinaldo', campo: 'calculoAguinaldo', label: 'Cálculo Aguinaldo', columna: 'Cálculo aguinaldo', tipo: 'bool' },
  { clave: 'extranjeroSinCurp', campo: 'extranjeroSinCurp', label: 'Extranjero Sin CURP', columna: 'Extranjero sin CURP', tipo: 'bool' },
  { clave: 'avisosPendientes', campo: 'avisosPendientes', label: 'Avisos Pendientes IMSS', columna: 'Aviso pendiente baja IMSS', tipo: 'enumStr', etiquetas: ETIQUETAS_AVISOS },
  { clave: 'tipoContratoId', campo: 'tipoContratoId', label: 'Tipo de Contrato', columna: 'Tipo de contrato', tipo: 'catalogo', catalogo: 'tiposContrato', muestra: 'Descripcion' },
  { clave: 'tipoPeriodoId', campo: 'tipoPeriodoId', label: 'Tipo de Periodo', columna: 'Tipo de periodo', tipo: 'catalogo', catalogo: 'tiposPeriodo', muestra: 'nombre' },
  { clave: 'formaDePagoId', campo: 'formaDePagoId', label: 'Método de Pago', columna: 'Método de pago', tipo: 'catalogo', catalogo: 'formasDePago', muestra: 'descripcion' },
  { clave: 'turnoId', campo: 'turnoId', label: 'Turno de Trabajo', columna: 'Turno de trabajo', tipo: 'catalogo', catalogo: 'turnos', muestra: 'nombre' },
  { clave: 'tipoPrestacionId', campo: 'tipoPrestacionId', label: 'Tipo de Prestación', columna: 'Tipo de prestación', tipo: 'catalogo', catalogo: 'tiposPrestacion', muestra: 'nombre' },
  { clave: 'registroPatronalId', campo: 'registroPatronalId', label: 'Registro Patronal', columna: 'Registro patronal del IMSS', tipo: 'catalogo', catalogo: 'registrosPatronales', muestra: 'registroPatronalIMSS' },
  { clave: 'tipoRegimenId', campo: 'tipoRegimenId', label: 'Tipo de Régimen', columna: 'Tipo de régimen', tipo: 'catalogo', catalogo: 'tiposRegimen', muestra: 'descripcion' },
  // Estos dos no existen en el layout externo: se eligen una sola vez en la
  // pantalla y se aplican a todos los empleados del archivo.
  { clave: 'regimenFiscalId', campo: 'regimenFiscalId', label: 'Régimen Fiscal', columna: null, general: true, tipo: 'catalogo', catalogo: 'regimenFiscal', muestra: 'descripcion' },
  { clave: 'tipoJornadaId', campo: 'tipoJornadaId', label: 'Tipo de Jornada', columna: null, general: true, tipo: 'catalogo', catalogo: 'tiposJornada', muestra: 'Descripcion' },
  { clave: 'bancoId', campo: 'bancoPagoElectronicoId', label: 'Banco', columna: 'Banco para pago electrónico', tipo: 'catalogo', catalogo: 'bancos', muestra: 'nombre' },
  { clave: 'departamento', campo: 'departamentoId', label: 'Departamento', columna: 'Departamento', tipo: 'creable', catalogo: 'departamentos', muestra: 'nombre' },
  { clave: 'puesto', campo: 'puestoId', label: 'Puesto', columna: 'Puesto', tipo: 'creable', catalogo: 'puestos', muestra: 'nombre' },
  { clave: 'cuentaBancaria', campo: 'numCuenta', label: 'Núm. de Cuenta', columna: 'Numero de cuenta para pago electrónico', tipo: 'texto' },
  { clave: 'clabe', campo: 'clabeInterbancaria', label: 'CLABE Interbancaria', columna: 'CLABE interbancaria', tipo: 'texto' },
  { clave: 'sucursalPagoElectronico', campo: 'sucursalPagoElectronico', label: 'Sucursal Pago Electrónico', columna: 'Sucursal para pago electrónico', tipo: 'texto' },
  { clave: 'umf', campo: 'umf', label: 'UMF', columna: 'UMF', tipo: 'texto' },
  { clave: 'expediente', campo: 'expediente', label: 'Expediente', columna: 'Expediente', tipo: 'texto' },
  { clave: 'afore', campo: 'afore', label: 'Afore', columna: 'Afore', tipo: 'numero' },
  { clave: 'numeroFONACOT', campo: 'numeroFONACOT', label: 'Número FONACOT', columna: 'Número FONACOT', tipo: 'numero' },
  { clave: 'extra1', campo: 'extra1', label: 'Campo Extra 1', columna: 'Campo extra 1', tipo: 'texto' },
  { clave: 'numExtra1', campo: 'numExtra1', label: 'Campo Extra Numérico 1', columna: 'Campo extra numérico 1', tipo: 'numero' },
]

// Nombres de campo aceptados por el backend al editar. El RFC no está: es la
// llave de emparejamiento y no se sobreescribe.
export const CAMPOS_POR_CLAVE = CAMPOS_SINCRONIZABLES.reduce((acc, c) => {
  acc[c.clave] = c
  return acc
}, {})

// ── Mapeo de una fila completa ───────────────────────────────────────────────

const RFC_VALIDO = /^[A-ZÑ&]{3,4}\d{6}[A-Z\d]{3}$/

/**
 * Traduce una fila del Excel externo al payload que espera el backend.
 *
 * @param {object} fila     fila cruda de sheet_to_row_object_array
 * @param {Map}    indice   índice de encabezados normalizados
 * @param {object} catalogos { tiposContrato, formasDePago, turnos, tiposPeriodo,
 *                             tiposPrestacion, bancos, registrosPatronales,
 *                             tiposRegimen, estados }
 * @param {object} generales valores elegidos en pantalla que aplican a todas las
 *                           filas, p. ej. { regimenFiscalId, tipoJornadaId }
 * @param {object} opciones  { incluirBajas } — con true la fila de baja se mapea
 *                           completa en vez de omitirse. Lo usa Nómina Histórica,
 *                           que da de alta (deshabilitados) a ex-empleados.
 * @returns {{ emp, erroresFila, avisosCatalogo, avisosPerdidos, omitirPorBaja, datosOmision, numFila,
 *             estatusExterno, esBaja, fechaBajaExterna }}
 */
export function mapearFilaExterna(fila, indice, catalogos = {}, generales = {}, opciones = {}) {
  const { incluirBajas = false } = opciones
  const leer = crearLector(fila, indice)
  const erroresFila = []
  const avisosCatalogo = []
  const emp = {}
  const numFila = fila['__rowNum__'] !== undefined ? fila['__rowNum__'] + 1 : 2

  const {
    tiposContrato = [], formasDePago = [], turnos = [], tiposPeriodo = [],
    tiposPrestacion = [], bancos = [], registrosPatronales = [],
    tiposRegimen = [], estados = [],
  } = catalogos

  // ── Identificación ─────────────────────────────────────────────────────────
  const codigo = leer('Código')
  const nombre = leer('Nombre')
  const apellidoPaterno = leer('Apellido paterno')
  const apellidoMaterno = leer('Apellido materno')
  const rfc = leer('RFC')
  const curp = leer('CURP')

  // ── Baja / reingreso ───────────────────────────────────────────────────────
  const fechaBajaRaw = leer('Fecha de baja')
  const fechaReingresoRaw = leer('Fecha de reingreso')
  const estatusRaw = leer('Estatus empleado')
  const causaBaja = leer('Causa de la última baja')

  // La fecha de baja solo se informa: no entra a `emp`. Ver empleadosFaltantes.js
  // (Nómina Histórica) para por qué un ex-empleado se crea sin fechaBaja.
  const fechaBajaExterna = parsearFechaExterna(fechaBajaRaw)
  const esBaja = debeOmitirsePorBaja({
    estatus: estatusRaw,
    fechaBaja: fechaBajaExterna,
    fechaReingreso: parsearFechaExterna(fechaReingresoRaw),
  })
  const datosEstatus = { estatusExterno: estatusRaw, esBaja, fechaBajaExterna }

  if (esBaja && !incluirBajas) {
    return {
      ...datosEstatus,
      omitirPorBaja: true,
      numFila,
      datosOmision: {
        'Código': codigo,
        'Nombre': [nombre, apellidoPaterno, apellidoMaterno].filter(Boolean).join(' '),
        'RFC': rfc,
        'Estatus': estatusRaw,
        'Fecha de baja': fechaBajaRaw,
        'Causa de la última baja': causaBaja,
      },
      emp: null, erroresFila: [], avisosCatalogo: [], avisosPerdidos: 0,
    }
  }

  // ── Obligatorios ───────────────────────────────────────────────────────────
  const queEste = (valor, campo) => {
    if (!valor) erroresFila.push(`${campo} está vacío`)
  }
  queEste(nombre, 'Nombre')
  queEste(apellidoPaterno, 'Apellido paterno')
  queEste(rfc, 'RFC')
  queEste(leer('Fecha de alta'), 'Fecha de alta')

  if (rfc) {
    const rfcMayus = rfc.toUpperCase()
    if (!RFC_VALIDO.test(rfcMayus)) erroresFila.push('RFC formato inválido')
    else emp.rfc = rfcMayus
  }

  if (codigo !== undefined) emp.numeroClave = codigo
  if (nombre) emp.nombre = nombre
  if (apellidoPaterno) emp.apellidoPaterno = apellidoPaterno
  if (apellidoMaterno) emp.apellidoMaterno = apellidoMaterno
  if (curp) emp.curp = curp.toUpperCase()

  // ── Fechas ─────────────────────────────────────────────────────────────────
  const guardarFecha = (columna, campo, obligatorio) => {
    const raw = leer(columna)
    if (!raw) return
    const fecha = parsearFechaExterna(raw)
    if (!fecha) {
      if (obligatorio) erroresFila.push(`${columna} formato inválido`)
      return
    }
    emp[campo] = fecha
  }
  guardarFecha('Fecha de alta', 'fechaAlta', true)
  guardarFecha('Fecha de reingreso', 'fechaReingreso')
  guardarFecha('Fecha de nacimiento', 'fechaNacimiento')
  guardarFecha('Fecha del salario diario', 'fechaSalarioDiario')
  guardarFecha('Fecha SBC parte fija', 'fechaSalarioIntegrado')
  guardarFecha('Fecha salario variable', 'fechaSalarioVariable')
  guardarFecha('Fecha salario promedio', 'fechaSalarioPromedio')

  // ── Números ────────────────────────────────────────────────────────────────
  const guardarNumero = (columna, campo, obligatorio) => {
    const raw = leer(columna)
    if (raw === undefined) return
    const n = parsearNumeroExterno(raw)
    if (n === undefined) {
      if (obligatorio) erroresFila.push(`${columna} no es número`)
      return
    }
    emp[campo] = n
  }
  guardarNumero('Salario diario', 'salarioDiario', true)
  guardarNumero('SBC parte fija', 'sbcParteFija', true)
  guardarNumero('Salario variable', 'salarioVariable')
  guardarNumero('Salario promedio', 'salarioPromedio')
  guardarNumero('Salario base liquidación', 'salarioBaseLiquidacion')
  guardarNumero('Saldo del ajuste al neto', 'saldoAjusteNeto')
  guardarNumero('Campo extra numérico 1', 'numExtra1')
  guardarNumero('Afore', 'afore')
  guardarNumero('Número FONACOT', 'numeroFONACOT')

  // ── Enumerados ─────────────────────────────────────────────────────────────
  const sexo = mapearSexoExterno(leer('Sexo'))
  if (sexo) emp.sexo = sexo

  const estadoCivil = mapearEstadoCivilExterno(leer('Estado Civil'))
  if (estadoCivil !== undefined) emp.estadoCivil = estadoCivil

  const basePago = mapearBasePagoExterno(leer('Base de pago'))
  if (basePago !== undefined) emp.basePago = basePago

  const baseCotizacion = mapearBaseCotizacionExterno(leer('Base de cotización'))
  if (baseCotizacion !== undefined) emp.baseDeCotizacion = baseCotizacion

  // ── Booleanos ──────────────────────────────────────────────────────────────
  const guardarBool = (columna, campo) => {
    const raw = leer(columna)
    if (raw !== undefined) emp[campo] = esSiExterno(raw)
  }
  guardarBool('Sindicalizado', 'sindicalizado')
  guardarBool('Cálculo PTU', 'calculoPTU')
  guardarBool('Cálculo aguinaldo', 'calculoAguinaldo')
  guardarBool('Extranjero sin CURP', 'extranjeroSinCurp')

  const { avisosPendientes, perdidos } = colapsarAvisosPendientes({
    modifSbc: leer('Aviso pendiente modificación SBC'),
    reingreso: leer('Aviso pendiente reingreso IMSS'),
    baja: leer('Aviso pendiente baja IMSS'),
    cambioBaseCotizacion: leer('Aviso pendiente cambio base cotización'),
  })
  if (avisosPendientes) emp.avisosPendientes = avisosPendientes

  // ── Catálogos ──────────────────────────────────────────────────────────────
  const resolver = (columna, catalogo, clave, etiquetaCatalogo) => {
    const valor = leer(columna)
    if (!valor) return
    emp[clave] = valor
    const encontrado = buscarEnCatalogo(valor, catalogo)
    if (encontrado) emp[`${clave}Id`] = encontrado._id
    else avisosCatalogo.push({ catalogo: etiquetaCatalogo, valor })
  }
  resolver('Tipo de contrato', tiposContrato, 'tipoContrato', 'Tipo de Contrato')
  resolver('Tipo de periodo', tiposPeriodo, 'tipoPeriodo', 'Tipo de Periodo')
  resolver('Método de pago', formasDePago, 'formaDePago', 'Método de Pago')
  resolver('Tipo de prestación', tiposPrestacion, 'tipoPrestacion', 'Tipo de Prestación')
  resolver('Tipo de régimen', tiposRegimen, 'tipoRegimen', 'Tipo de Régimen')
  resolver('Turno de trabajo', turnos, 'turno', 'Turno de Trabajo')
  resolver('Banco para pago electrónico', bancos, 'banco', 'Banco')

  const regPatronal = leer('Registro patronal del IMSS')
  if (regPatronal) {
    emp.registroPatronalIMSS = regPatronal
    const objetivo = normalizarRegistroPatronal(regPatronal)
    const encontrado = registrosPatronales.find(
      (r) => normalizarRegistroPatronal(r.registroPatronalIMSS) === objetivo
    )
    if (encontrado) emp.registroPatronalId = encontrado._id
    else avisosCatalogo.push({ catalogo: 'Registro Patronal', valor: regPatronal })
  }

  // ── Domicilio y nacimiento ─────────────────────────────────────────────────
  const direccion = leer('Dirección')
  if (direccion) emp.calle = direccion

  const cp = leer('CP')
  if (cp) emp.cp = cp

  const estadoDomVal = leer('Entidad federativa de domicilio')
  if (estadoDomVal) {
    emp.estadoDomicilio = estadoDomVal
    const encontrado = buscarEstado(estados, [estadoDomVal])
    if (encontrado) emp.estadoDomicilioId = encontrado._id
  }

  const estadoNacVal = leer('Entidad federativa de nacimiento')
  const estadoNac = resolverEstadoNacimiento(estadoNacVal, emp.curp, estados)
  if (estadoNac) emp.estadoNacimientoId = estadoNac._id
  else if (estadoNacVal) avisosCatalogo.push({ catalogo: 'Estado de Nacimiento', valor: estadoNacVal })

  const ciudadNac = leer('Ciudad de nacimiento')
  if (ciudadNac) emp.ciudadNacimientoId = ciudadNac

  // ── Texto libre ────────────────────────────────────────────────────────────
  const guardarTexto = (columna, campo) => {
    const valor = leer(columna)
    if (valor) emp[campo] = valor
  }
  guardarTexto('Num seguridad social', 'numSeguridadSocial')
  guardarTexto('Correo electrónico', 'correoElectronico')
  guardarTexto('Teléfono', 'telefono')
  guardarTexto('Nombre del padre', 'nombrePadre')
  guardarTexto('Nombre de la madre', 'nombreMadre')
  guardarTexto('UMF', 'umf')
  guardarTexto('Expediente', 'expediente')
  guardarTexto('CLABE interbancaria', 'clabe')
  guardarTexto('Numero de cuenta para pago electrónico', 'cuentaBancaria')
  guardarTexto('Sucursal para pago electrónico', 'sucursalPagoElectronico')
  guardarTexto('Campo extra 1', 'extra1')
  guardarTexto('Líder', 'liderCodigo')
  guardarTexto('Departamento', 'departamento')
  guardarTexto('Puesto', 'puesto')
  if (causaBaja) emp.ultimaBaja = causaBaja

  // Valores generales de la pantalla: van igual para todas las filas.
  for (const clave of Object.keys(generales)) {
    if (generales[clave]) emp[clave] = generales[clave]
  }

  return {
    ...datosEstatus,
    emp, erroresFila, avisosCatalogo, avisosPerdidos: perdidos,
    omitirPorBaja: false, datosOmision: null, numFila,
  }
}

// ── Emparejamiento ───────────────────────────────────────────────────────────

/**
 * Llave de nombre completo para emparejar una fila del archivo con un empleado
 * del sistema cuando el RFC no coincide. Sirve igual para el payload del Excel y
 * para el documento de Mongo porque ambos usan los mismos tres campos.
 * Devuelve '' cuando no hay nombre y apellido paterno: sin eso no se empareja.
 */
export function claveNombreEmpleado(e) {
  if (!e) return ''
  const nombre = normalizarTexto(e.nombre || '')
  const paterno = normalizarTexto(e.apellidoPaterno || '')
  const materno = normalizarTexto(e.apellidoMaterno || '')
  if (!nombre || !paterno) return ''
  return `${nombre}|${paterno}|${materno}`
}

/**
 * Índices para emparejar filas del archivo contra los empleados del sistema.
 * Se incluyen los deshabilitados a propósito: si un RFC o un nombre ya existe
 * aunque esté de baja, no hay que duplicarlo.
 */
export function construirIndicesEmpleados(empleados = []) {
  const porRfc = new Map()
  const porNombre = new Map()
  for (const emp of empleados) {
    if (emp.rfc) porRfc.set(String(emp.rfc).trim().toUpperCase(), emp)
    const llave = claveNombreEmpleado(emp)
    if (!llave) continue
    if (!porNombre.has(llave)) porNombre.set(llave, [])
    porNombre.get(llave).push(emp)
  }
  return { porRfc, porNombre }
}

/**
 * Empareja una fila del archivo con un empleado del sistema, en cascada:
 * primero por RFC y, si no aparece, por nombre + apellido paterno + materno.
 *
 * @param {object} emp      payload de mapearFilaExterna
 * @param {object} indices  resultado de construirIndicesEmpleados
 * @param {Set}    usados   ids de empleados que otra fila ya reclamó
 * @returns {{ existente, criterio, error }} `error` es un mensaje listo para el
 *          Excel de errores; cuando viene, la fila no se debe procesar.
 */
export function emparejarEmpleado(emp, indices, usados = new Set()) {
  const { porRfc, porNombre } = indices
  const nombreLegible = [emp.nombre, emp.apellidoPaterno, emp.apellidoMaterno].filter(Boolean).join(' ')

  let existente = emp.rfc ? porRfc.get(emp.rfc) : undefined
  let criterio = 'RFC'

  if (!existente) {
    const llave = claveNombreEmpleado(emp)
    const candidatos = llave ? porNombre.get(llave) || [] : []
    if (candidatos.length > 1) {
      return {
        existente: null,
        criterio: null,
        error: `Hay ${candidatos.length} empleados con el nombre "${nombreLegible}" y el RFC no coincide con ninguno; resuélvelo a mano`,
      }
    }
    if (candidatos.length === 1) {
      existente = candidatos[0]
      criterio = 'Nombre'
    }
  }

  if (!existente) return { existente: null, criterio: null, error: null }

  if (usados.has(String(existente._id))) {
    return {
      existente: null,
      criterio: null,
      error: `Otra fila del archivo ya apunta al mismo empleado (${existente.serieNumero || ''})`,
    }
  }

  // No hace falta revisar aquí que el RFC del archivo no sea de otro empleado:
  // si se llegó al emparejamiento por nombre es justamente porque ese RFC no
  // existe en el sistema. El caso de dos filas del archivo con el mismo RFC lo
  // ataja quien recorre las filas, y el backend lo revalida al escribir.
  return { existente, criterio, error: null }
}

// ── Comparación contra el empleado que ya está en el sistema ─────────────────
//
// Regla de fondo: una celda vacía del archivo externo NO borra nada. Solo hay
// cambio cuando el archivo trae un valor y ese valor difiere del actual. El
// backend aplica exactamente el mismo criterio al construir el $set.

export const VACIO = '—'

const textoONada = (v) => (v === undefined || v === null || v === '' ? VACIO : String(v))

// Las fechas se guardan como medianoche UTC (Mongoose castea 'YYYY-MM-DD' así),
// de modo que hay que leerlas en UTC. Con la hora local, en México (UTC-6) toda
// fecha retrocedería un día y el diff marcaría cambios en cada pasada.
const fechaISO = (d) => (d ? moment.utc(d).format('YYYY-MM-DD') : '')

/**
 * Valor actual del empleado para un campo, en forma comparable y legible.
 * `indices` es { nombreCatalogo: Map(idString -> documento) }.
 */
export function valorActualEmpleado(cfg, empleado, indices = {}) {
  switch (cfg.tipo) {
    case 'fecha': {
      const iso = fechaISO(empleado[cfg.campo])
      return { comparable: iso, texto: iso ? moment.utc(iso).format('DD/MM/YYYY') : VACIO }
    }
    case 'bool': {
      const b = !!empleado[cfg.campo]
      return { comparable: b, texto: b ? 'Sí' : 'No' }
    }
    case 'numero': {
      const n = empleado[cfg.campo]
      return {
        comparable: n === undefined || n === null || n === '' ? '' : String(Number(n)),
        texto: textoONada(n),
      }
    }
    case 'enumNum': {
      const n = empleado[cfg.campo]
      return {
        comparable: n === undefined || n === null ? '' : String(n),
        texto: cfg.etiquetas[n] || VACIO,
      }
    }
    case 'enumStr': {
      const v = empleado[cfg.campo] || ''
      return { comparable: v, texto: cfg.etiquetas[v] || VACIO }
    }
    case 'catalogo':
    case 'creable': {
      const id = empleado[cfg.campo]
      const item = id ? (indices[cfg.catalogo] && indices[cfg.catalogo].get(String(id))) : null
      const nombre = item ? String(item[cfg.muestra] ?? '') : ''
      return { comparable: id ? String(id) : '', texto: nombre || VACIO, nombre }
    }
    case 'telefono': {
      const t = ((empleado.telefonos || [])[0] || {}).numero || ''
      return { comparable: t, texto: textoONada(t) }
    }
    default: {
      const v = empleado[cfg.campo]
      return { comparable: v === undefined || v === null ? '' : String(v), texto: textoONada(v) }
    }
  }
}

/**
 * Valor que trae el archivo para un campo. Devuelve null cuando la columna viene
 * vacía: en ese caso el campo no se toca.
 */
export function valorDelArchivo(cfg, emp, indices = {}) {
  const crudo = emp[cfg.clave]
  if (crudo === undefined || crudo === null || crudo === '') return null

  switch (cfg.tipo) {
    case 'fecha':
      return { comparable: String(crudo), texto: moment.utc(crudo).format('DD/MM/YYYY') }
    case 'bool':
      return { comparable: !!crudo, texto: crudo ? 'Sí' : 'No' }
    case 'numero':
      return { comparable: String(Number(crudo)), texto: String(crudo) }
    case 'enumNum':
    case 'enumStr':
      return { comparable: String(crudo), texto: cfg.etiquetas[crudo] || String(crudo) }
    case 'catalogo': {
      const item = indices[cfg.catalogo] && indices[cfg.catalogo].get(String(crudo))
      return {
        comparable: String(crudo),
        texto: item ? String(item[cfg.muestra] ?? '') : String(crudo),
      }
    }
    case 'creable': {
      // El archivo trae el nombre y el departamento/puesto puede no existir aún
      // (el backend lo crea). Por eso se compara por nombre, no por id.
      return { comparable: `nombre:${normalizarTexto(crudo)}`, texto: String(crudo), nombre: String(crudo) }
    }
    default:
      return { comparable: String(crudo), texto: String(crudo) }
  }
}

function camposSonIguales(cfg, actual, nuevo) {
  if (cfg.tipo === 'creable') {
    return normalizarTexto(actual.nombre || '') === normalizarTexto(nuevo.nombre || '')
  }
  return actual.comparable === nuevo.comparable
}

/**
 * Diff completo de una fila del archivo contra el empleado que ya existe.
 * @returns {{ cambios: string[], detalle: { [clave]: { antes, despues } } }}
 */
export function compararConEmpleado(emp, empleado, indices = {}) {
  const cambios = []
  const detalle = {}
  for (const cfg of CAMPOS_SINCRONIZABLES) {
    const nuevo = valorDelArchivo(cfg, emp, indices)
    if (!nuevo) continue
    const actual = valorActualEmpleado(cfg, empleado, indices)
    if (camposSonIguales(cfg, actual, nuevo)) continue
    cambios.push(cfg.clave)
    detalle[cfg.clave] = { antes: actual.texto, despues: nuevo.texto }
  }
  return { cambios, detalle }
}

// Agrupa los avisos de catálogo de todas las filas en
// [{ catalogo, valores: [{ valor, filas }] }] para mostrarlos de forma compacta.
export function agruparAvisosCatalogo(listaDeAvisos) {
  const porCatalogo = new Map()
  for (const { catalogo, valor } of listaDeAvisos) {
    if (!porCatalogo.has(catalogo)) porCatalogo.set(catalogo, new Map())
    const valores = porCatalogo.get(catalogo)
    valores.set(valor, (valores.get(valor) || 0) + 1)
  }
  return [...porCatalogo.entries()].map(([catalogo, valores]) => ({
    catalogo,
    valores: [...valores.entries()]
      .map(([valor, filas]) => ({ valor, filas }))
      .sort((a, b) => b.filas - a.filas),
  }))
}
